import { Flex, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { styles, StyledMenuButton } from './MenuButton.styles';
import { Hamburger } from '@withjoy/joykit/icons';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body2ToNavigationOverride, label4ToNavigationOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { pxToRem } from '@withjoy/joykit/theme';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { useTranslation } from '@shared/core';
import { useMenuButtonScroll } from './useMenuButtonScroll';
import { animationTransition } from '@shared/utils/animationTransition';
import { isLightColor, parseColor, toRgbArray } from '@shared/utils/color';

interface Props
  extends Readonly<{
    onClick: () => void;
    isCustomPage?: boolean;
    topOffset?: number;
  }> {}

export const MenuButton: React.FC<Props> = ({ onClick, isCustomPage, topOffset = 0 }) => {
  const { tq } = useTranslation('eventMenu');
  const { pageColor, pageFontColor, accentColor } = useColorPaletteProvider();
  const { hasScrolled } = useMenuButtonScroll();

  return (
    <StyledMenuButton
      {...styles.button(isCustomPage, topOffset)}
      onClick={onClick}
      aria-label={tq`Menu`}
      paddingY={0}
      paddingX={hasScrolled ? pxToRem(12) : pxToRem(3)}
      height={hasScrolled ? pxToRem(56) : pxToRem(42)}
      background={pageColor}
      borderRadius={hasScrolled ? pxToRem(28) : pxToRem(12)}
      display="flex"
      alignItems="center"
      justifyContent="center"
      border="2px solid"
      borderColor={hasScrolled ? accentColor : 'transparent'}
      boxShadow={hasScrolled ? '0px 4px 16px 0px rgba(0, 0, 0, 0.07), 0px 7px 27px 0px rgba(44, 41, 37, 0.06)' : 'none'}
      transition={animationTransition('box-shadow', 'border-color', 'padding', 'height', 'border-radius', 'background')}
      _hover={
        pageColor
          ? {
              background: isLightColor(...toRgbArray(pageColor)) ? parseColor(pageColor)?.darken(0.1).hex() : parseColor(pageColor)?.brighten(0.1).hex(),
            }
          : {}
      }
    >
      <GuestSiteTypographyOverride override={label4ToNavigationOverride}>
        <Flex
          gap={3}
          overflow="hidden"
          alignItems="center"
          color={hasScrolled ? accentColor : pageFontColor}
        >
          <Hamburger size={32} />
          {hasScrolled && (
            <GuestSiteTypographyOverride override={body2ToNavigationOverride}>
              <TextV2
                typographyVariant="body2"
                textTransform="uppercase"
                fontWeight={500}
                fontSize={pxToRem(18)}
              >
                {tq`Menu`}
              </TextV2>
            </GuestSiteTypographyOverride>
          )}
        </Flex>
      </GuestSiteTypographyOverride>
    </StyledMenuButton>
  );
};

export default MenuButton;
